import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Leaf, ArrowRight, Calculator, Wind, Cloud, BarChart3, BrainCircuit,
  FileBarChart, TrendingUp, ShieldCheck, Recycle, Zap, CheckCircle2,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1556983852-43bf21186b2a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzdXN0YWluYWJsZSUyMGdyZWVuJTIwYnVpbGRpbmclMjBjb25zdHJ1Y3Rpb258ZW58MHx8fHwxNzgwMjI0MjU5fDA&ixlib=rb-4.1.0&q=85";
const DASH_IMG = "https://static.prod-images.emergentagent.com/jobs/9f587cd0-f1dc-4f2b-8580-50f35b2a5e36/images/0d56df1717586c4202cdab25fb25249d560f42d61859708e1a66434854bc310b.png";

const FEATURES = [
  { icon: Calculator, title: "Green HPP", desc: "Hitung HPP tradisional & Green HPP per batch dan per unit/m³ secara akurat." },
  { icon: Leaf, title: "Environmental Costing", desc: "Integrasikan biaya limbah, energi, pengendalian, dan reuse/recycle ke dalam HPP." },
  { icon: Cloud, title: "Carbon Emission", desc: "Hitung emisi CO₂ dari material, listrik, BBM, dan air beserta biaya karbonnya." },
  { icon: BarChart3, title: "ESG Dashboard", desc: "Indikator & grafik ESG yang mudah dipahami manajemen dan reviewer." },
  { icon: BrainCircuit, title: "AI Decision Support", desc: "Deteksi anomali, cost driver, simulasi skenario, dan rekomendasi efisiensi." },
  { icon: FileBarChart, title: "Management Reports", desc: "Ekspor laporan PDF & Excel untuk akuntansi, audit, ESG, dan keputusan." },
];

const BENEFITS = [
  { icon: TrendingUp, t: "Akurasi HPP Meningkat" },
  { icon: BarChart3, t: "Transparansi Biaya" },
  { icon: Recycle, t: "Efisiensi Material" },
  { icon: Zap, t: "Pengendalian Energi" },
  { icon: Leaf, t: "Dukungan ESG" },
  { icon: ShieldCheck, t: "Audit Lebih Mudah" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Institution top bar */}
      <div className="bg-slate-900 text-slate-300 text-xs">
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
          <span className="flex items-center gap-2 font-medium">
            <Leaf className="h-3.5 w-3.5 text-green-400" /> Politeknik Negeri Manado
          </span>
          <span className="hidden sm:inline tracking-wide">Green Costing Information System</span>
        </div>
      </div>

      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-green-700 flex items-center justify-center">
              <Leaf className="h-5 w-5 text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-slate-900">SIA-HPP GREENCON</div>
              <div className="text-[10px] tracking-wider text-green-700 font-semibold">GREEN COSTING INFORMATION SYSTEM</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="#fitur" className="hidden sm:inline text-sm text-slate-600 hover:text-slate-900">Fitur</a>
            <a href="#manfaat" className="hidden sm:inline text-sm text-slate-600 hover:text-slate-900">Manfaat</a>
            <Link to="/login">
              <Button className="bg-green-700 hover:bg-green-800" data-testid="nav-login-btn">Masuk ke Sistem</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-24">
          <div className="animate-fade-in">
            <span className="inline-flex items-center gap-2 rounded-full bg-green-50 border border-green-200 px-3 py-1 text-xs font-semibold text-green-800">
              <Leaf className="h-3.5 w-3.5" /> Green Accounting · AI · Konstruksi Ramah Lingkungan
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 mt-5 leading-[1.05]">
              SIA-HPP <span className="text-green-700">GREENCON</span>
            </h1>
            <p className="text-lg text-slate-700 mt-4 font-medium">
              Sistem Informasi Akuntansi HPP Berbasis Green Accounting dan AI untuk Produk Konstruksi Ramah Lingkungan.
            </p>
            <p className="text-base text-slate-500 mt-3 max-w-xl">
              Platform digital untuk menghitung HPP produksi, biaya lingkungan, emisi karbon, dashboard ESG, dan rekomendasi efisiensi berbasis AI.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/login">
                <Button size="lg" className="bg-green-700 hover:bg-green-800 h-12 px-6" data-testid="hero-login-btn">
                  Masuk ke Sistem <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <a href="#fitur">
                <Button size="lg" variant="outline" className="h-12 px-6 border-slate-300" data-testid="hero-features-btn">
                  Lihat Fitur
                </Button>
              </a>
            </div>
          </div>
          <div className="relative animate-fade-in">
            <div className="absolute -inset-4 bg-green-600/10 rounded-3xl blur-2xl" />
            <img src={HERO_IMG} alt="Green Building" className="relative rounded-2xl shadow-2xl border border-slate-200 w-full object-cover h-[420px]" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="fitur" className="bg-slate-50 border-y border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.1em] uppercase text-green-700">Fitur Utama</p>
            <h2 className="font-heading text-3xl font-bold text-slate-900 mt-2">Satu sistem untuk costing & keberlanjutan</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="h-11 w-11 rounded-lg bg-green-50 text-green-700 flex items-center justify-center">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading font-semibold text-slate-900 mt-4">{f.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard preview */}
      <section className="py-20 max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
        <img src={DASH_IMG} alt="Dashboard" className="rounded-2xl shadow-xl border border-slate-200 w-full" />
        <div>
          <p className="text-xs font-semibold tracking-[0.1em] uppercase text-green-700">Dashboard ESG</p>
          <h2 className="font-heading text-3xl font-bold text-slate-900 mt-2">Keputusan cepat berbasis data</h2>
          <p className="text-slate-500 mt-4">
            Pantau Green HPP, total biaya lingkungan, emisi CO₂, waste rate, hingga rekomendasi harga jual dalam satu tampilan yang siap dipresentasikan kepada pimpinan, reviewer penelitian, dan mitra industri.
          </p>
          <ul className="mt-6 space-y-3">
            {["HPP per batch & per unit/m³", "Komposisi biaya BBB, BTKL, BOP, Lingkungan", "Tren HPP & emisi karbon", "AI insight & deteksi anomali"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-slate-700">
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Benefits */}
      <section id="manfaat" className="bg-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs font-semibold tracking-[0.1em] uppercase text-green-400">Manfaat Industri</p>
            <h2 className="font-heading text-3xl font-bold text-white mt-2">Nilai nyata untuk perusahaan Anda</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {BENEFITS.map((b) => (
              <div key={b.t} className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl p-5">
                <div className="h-11 w-11 rounded-lg bg-green-600/20 text-green-400 flex items-center justify-center shrink-0">
                  <b.icon className="h-5 w-5" />
                </div>
                <span className="text-white font-medium">{b.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-green-700 flex items-center justify-center">
              <Leaf className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-heading font-bold text-white text-sm">SIA-HPP GREENCON</div>
              <div className="text-[11px]">Politeknik Negeri Manado · Green Costing Information System</div>
            </div>
          </div>
          <p className="text-xs text-center sm:text-right">© {new Date().getFullYear()} Politeknik Negeri Manado · SIA-HPP GREENCON<br className="hidden sm:block" /> Sistem Informasi Akuntansi Green HPP & AI</p>
        </div>
      </footer>
    </div>
  );
}
